// The left rail's entries (WEB_PLAN.md §1.1 layout, §5.0 routes), grouped like the iOS menu sheet (Work, Company).
// Reps see their reduced console (§3.1) with "My" labels, plus the company documents everyone can read.

import {
  ChartColumn,
  Files,
  LayoutDashboard,
  ListChecks,
  MapPinned,
  Map as MapIcon,
  Settings,
  SquareKanban,
  Store,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { Role } from '@/contracts/wire';
import { permissionsFor, type ConsoleRoute } from '@/session/permissions';

export interface NavItem {
  route: ConsoleRoute;
  /** Where the link goes. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Path prefixes that mark this entry active (besides `to`). */
  matches: readonly string[];
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

const DASHBOARD: NavItem = { route: 'dashboard', to: '/', label: 'Dashboard', icon: LayoutDashboard, matches: [] };
const STORES: NavItem = { route: 'stores', to: '/stores', label: 'Stores', icon: Store, matches: ['/stores'] };
const FOLLOW_UPS: NavItem = {
  route: 'followUps',
  to: '/follow-ups',
  label: 'Follow-ups',
  icon: ListChecks,
  matches: ['/follow-ups'],
};
const MAP: NavItem = { route: 'map', to: '/map', label: 'Map', icon: MapIcon, matches: ['/map'] };
const PIPELINE: NavItem = {
  route: 'pipeline',
  to: '/pipeline',
  label: 'Pipeline',
  icon: SquareKanban,
  matches: ['/pipeline'],
};
const TERRITORIES: NavItem = {
  route: 'territories',
  to: '/territories',
  label: 'Territories',
  icon: MapPinned,
  matches: ['/territories'],
};
const TEAM: NavItem = { route: 'team', to: '/team', label: 'Team', icon: Users, matches: ['/team'] };
const DOCUMENTS: NavItem = {
  route: 'documents',
  to: '/documents',
  label: 'Documents',
  icon: Files,
  matches: ['/documents'],
};
const REPORTS: NavItem = {
  route: 'reports',
  to: '/reports',
  label: 'Reports',
  icon: ChartColumn,
  matches: ['/reports', '/leaderboard'],
};
const SETTINGS: NavItem = {
  route: 'settings',
  to: '/settings/company',
  label: 'Settings',
  icon: Settings,
  matches: ['/settings'],
};

/** Rail groups for a role; entries the role cannot open are left out. */
export function navGroupsFor(role: Role): NavGroup[] {
  const perms = permissionsFor(role);
  if (!perms.isManager) {
    return [
      {
        id: 'mine',
        label: 'My work',
        items: [
          { ...DASHBOARD, label: 'My numbers' },
          { ...STORES, label: 'My stores' },
          { ...FOLLOW_UPS, label: 'My follow-ups' },
        ],
      },
      { id: 'company', label: 'Company', items: [DOCUMENTS] },
    ];
  }
  const groups: NavGroup[] = [
    { id: 'work', label: 'Work', items: [DASHBOARD, STORES, FOLLOW_UPS, MAP, PIPELINE, TERRITORIES] },
    { id: 'company', label: 'Company', items: [TEAM, REPORTS, DOCUMENTS, SETTINGS] },
  ];
  return groups
    .map((g) => ({ ...g, items: g.items.filter((item) => perms.canSeeConsole[item.route]) }))
    .filter((g) => g.items.length > 0);
}

/** Whether `item` is the current page's entry. The dashboard matches "/" exactly; others by path prefix. */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (item.to === '/') return path === '/';
  const prefixes = item.matches.length ? item.matches : [item.to];
  return prefixes.some((p) => path === p || path.startsWith(`${p}/`));
}
